import { Box } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { BoolIcon, MarginIcon } from '~/assets/workspace-icons';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import { useStructureToolGating } from '~/components/toolbar/shared/useStructureToolGating';
import { operationPanelState } from '~/state/atoms/toolsAtoms';
import { useRecoilState } from 'recoil';

export default function OperationsSection() {
    const {
        selectedRoiNumber,
        isStructureSetApproved,
        structureSetEditDisabled,
    } = useStructureToolGating();
    const intl = useIntl();
    const [panelState, setPanelState] = useRecoilState(operationPanelState);

    // Boolean and margin always need a writable structure set: even when
    // targeting an existing ROI the new contour data has to land somewhere,
    // and an approved structure set rejects creating or modifying ROIs.
    const isDisabled = structureSetEditDisabled;
    const isRoiDisabled = isDisabled || selectedRoiNumber === -1;
    const approvedReason = isStructureSetApproved
        ? intl.formatMessage({ id: 'tool.disabled.approved' })
        : undefined;

    const handleBooleanToggle = useCallback(() => {
        setPanelState((prev) =>
            prev.type === 'boolean-operation'
                ? { type: 'closed' }
                : { type: 'boolean-operation' }
        );
    }, [setPanelState]);

    const handleMarginToggle = useCallback(() => {
        setPanelState((prev) =>
            prev.type === 'margin'
                ? { type: 'closed' }
                : { type: 'margin', mode: 'symmetric' }
        );
    }, [setPanelState]);

    // Disabling the ToolbarButton doesn't close an already-open panel.
    useEffect(() => {
        if (
            panelState.type === 'boolean-operation' &&
            (isDisabled || isStructureSetApproved)
        ) {
            setPanelState({ type: 'closed' });
        } else if (
            panelState.type === 'margin' &&
            (isRoiDisabled || isStructureSetApproved)
        ) {
            setPanelState({ type: 'closed' });
        }
    }, [
        panelState.type,
        isDisabled,
        isRoiDisabled,
        isStructureSetApproved,
        setPanelState,
    ]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
            }}
        >
            <ToolbarButton
                icon={<BoolIcon />}
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({
                        id: 'toolbar.booleanOperation.tooltip',
                    }),
                    approvedReason
                )}
                tooltipPlacement="left"
                selected={panelState.type === 'boolean-operation'}
                disabled={isDisabled || isStructureSetApproved}
                onClick={handleBooleanToggle}
            />

            <ToolbarButton
                icon={<MarginIcon />}
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({ id: 'toolbar.margin.tooltip' }),
                    approvedReason
                )}
                tooltipPlacement="left"
                selected={panelState.type === 'margin'}
                disabled={isRoiDisabled || isStructureSetApproved}
                onClick={handleMarginToggle}
            />
        </Box>
    );
}
